/**
 * Staff API functions + TanStack Query hooks.
 *
 * Staff creation also creates a Firebase Auth account with custom claims.
 * Deactivation disables the Auth account.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { api } from '@core/api/client';
import type { components } from '@api/index';
import type { EntityListResult } from '@/components/entity/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Augment the generated type with 11E.4 fields not yet regenerated into
// shared/api-types/index.ts (same pattern as StudentResponse.billing_summary
// in people/api/students.ts).
export type DayOfWeek =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

/** One availability block (11F.7). Multiple per day = split shift. */
export interface WorkingHoursBlock {
  day_of_week: DayOfWeek;
  start_time: string; // "HH:MM"
  end_time: string; // "HH:MM"
}

type _GeneratedStaffResponse = components['schemas']['StaffResponse'];
export type StaffResponse = _GeneratedStaffResponse & {
  bio?: string | null;
  expertise?: string[] | null;
  /** @deprecated 11F.7 — superseded by `working_hours_schedule`. */
  working_hours?: Record<string, string> | null;
  working_hours_schedule?: WorkingHoursBlock[] | null;
};

// ── Pay rates (11F.7) ─────────────────────────────────────────────────────

export interface StaffPayRate {
  subject_id: string;
  subject_name?: string | null;
  duration_minutes: number;
  /** Minor units (paise), like every other money field. */
  amount: number;
}

export interface StaffPayRatesResponse {
  staff_id: string;
  rates: StaffPayRate[];
}

// ── Timesheet (11F.7) — derived from taught sessions, never stored ────────

export interface TimesheetRow {
  session_id: string;
  date: string;
  day_of_week: string;
  starts_at: string;
  ends_at: string;
  duration_minutes: number;
  subject_id?: string | null;
  subject_name?: string | null;
  branch_id?: string | null;
  student_count: number;
  attendance_marked: boolean;
}

export interface TimesheetResponse {
  staff_id: string;
  from_date: string;
  to_date: string;
  rows: TimesheetRow[];
  classes_taken: number;
  hours_taught: number;
  /** null when the staff member has no configured working hours. */
  hours_available: number | null;
  /** null whenever hours_available is null/zero — never a fabricated 0%. */
  occupancy_pct: number | null;
  total_students: number;
}
export type PaginatedStaff = components['schemas']['PaginatedResult_StaffResponse_'];

export interface StaffCreate {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  phone?: string | null;
  role?: 'admin' | 'manager' | 'staff';
  photo_url?: string | null;
  branch_ids?: string[];
  subject_ids?: string[];
  hire_date?: string | null;
  staff_type?: string | null;
  bio?: string | null;
  expertise?: string[];
  working_hours?: Record<string, string> | null;
}

export interface StaffUpdate {
  first_name?: string;
  last_name?: string;
  phone?: string | null;
  role?: 'admin' | 'manager' | 'staff';
  photo_url?: string | null;
  branch_ids?: string[];
  subject_ids?: string[];
  hire_date?: string | null;
  staff_type?: string | null;
  bio?: string | null;
  expertise?: string[];
  working_hours?: Record<string, string> | null;
  /** 11F.7 — structured weekly availability. */
  working_hours_schedule?: WorkingHoursBlock[] | null;
}

export interface ListStaffParams {
  status?: string;
  role?: string;
  limit?: number;
}

export interface ListStaffPagedParams {
  status?: string;
  role?: string;
  staff_type?: string;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const staffKeys = {
  all: ['staff'] as const,
  list: (params: ListStaffParams) => ['staff', 'list', params] as const,
  pagedList: (params: ListStaffPagedParams) => ['staff', 'paged', params] as const,
  detail: (id: string) => ['staff', 'detail', id] as const,
  payRates: (id: string) => ['staff', 'pay-rates', id] as const,
  timesheet: (id: string, from: string, to: string) =>
    ['staff', 'timesheet', id, from, to] as const,
};

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export async function listStaff(
  params: ListStaffParams & { cursor?: string },
): Promise<PaginatedStaff> {
  return api.get<PaginatedStaff>('/v1/staff', {
    query: {
      status: params.status || undefined,
      role: params.role || undefined,
      cursor: params.cursor || undefined,
      limit: params.limit ?? 20,
    },
  });
}

export async function listStaffPaged(
  params: ListStaffPagedParams,
): Promise<EntityListResult<StaffResponse>> {
  return api.get<EntityListResult<StaffResponse>>('/v1/staff', {
    query: {
      status: params.status || undefined,
      role: params.role || undefined,
      staff_type: params.staff_type || undefined,
      search: params.search || undefined,
      sort: params.sort || undefined,
      order: params.order || undefined,
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
    },
  });
}

export async function getStaff(id: string): Promise<StaffResponse> {
  return api.get<StaffResponse>(`/v1/staff/${id}`);
}

export async function createStaff(body: StaffCreate): Promise<StaffResponse> {
  return api.post<StaffResponse>('/v1/staff', body);
}

export async function updateStaff(id: string, body: StaffUpdate): Promise<StaffResponse> {
  return api.patch<StaffResponse>(`/v1/staff/${id}`, body);
}

export async function deactivateStaff(id: string): Promise<StaffResponse> {
  return api.post<StaffResponse>(`/v1/staff/${id}/deactivate`);
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useStaff(params: ListStaffParams = {}) {
  return useInfiniteQuery<
    PaginatedStaff,
    Error,
    InfiniteData<PaginatedStaff>,
    ReturnType<typeof staffKeys.list>,
    string | undefined
  >({
    queryKey: staffKeys.list(params),
    queryFn: ({ pageParam }) => listStaff({ ...params, cursor: pageParam }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });
}

export function useStaffList(params: ListStaffPagedParams) {
  return useQuery({
    queryKey: staffKeys.pagedList(params),
    queryFn: () => listStaffPaged(params),
    placeholderData: (prev) => prev,
  });
}

/** staleTime:0 — always re-fetch to fix edit pre-load stale-cache bug. */
export function useStaffMember(id: string) {
  return useQuery({
    queryKey: staffKeys.detail(id),
    queryFn: () => getStaff(id),
    enabled: Boolean(id),
    staleTime: 0,
  });
}

export function useCreateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createStaff,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffKeys.all });
    },
  });
}

export function useUpdateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: StaffUpdate }) =>
      updateStaff(id, body),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: staffKeys.all });
      qc.invalidateQueries({ queryKey: staffKeys.detail(id) });
    },
  });
}

export function useDeactivateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deactivateStaff,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffKeys.all });
    },
  });
}


// ---------------------------------------------------------------------------
// 11F.7 — pay rates + timesheet
// ---------------------------------------------------------------------------

export async function getStaffPayRates(id: string): Promise<StaffPayRatesResponse> {
  return api.get<StaffPayRatesResponse>(`/v1/staff/${id}/pay-rates`);
}

export async function putStaffPayRates(
  id: string,
  rates: StaffPayRate[],
): Promise<StaffPayRatesResponse> {
  // subject_name is resolved server-side on every write, so it is
  // deliberately not sent — doing so would let a stale client label win.
  return api.put<StaffPayRatesResponse>(`/v1/staff/${id}/pay-rates`, {
    rates: rates.map(({ subject_id, duration_minutes, amount }) => ({
      subject_id,
      duration_minutes,
      amount,
    })),
  });
}

export function useStaffPayRates(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: staffKeys.payRates(id ?? '__none__'),
    queryFn: () => getStaffPayRates(id as string),
    enabled: Boolean(id) && enabled,
    staleTime: 60_000,
  });
}

export function useUpdateStaffPayRates(staffId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rates: StaffPayRate[]) => putStaffPayRates(staffId, rates),
    onSuccess: (data) => {
      qc.setQueryData(staffKeys.payRates(staffId), data);
    },
  });
}

export async function getStaffTimesheet(
  id: string,
  from: string,
  to: string,
): Promise<TimesheetResponse> {
  return api.get<TimesheetResponse>(`/v1/staff/${id}/timesheet`, {
    query: { from, to },
  });
}

export function useStaffTimesheet(
  id: string | undefined,
  from: string,
  to: string,
) {
  return useQuery({
    queryKey: staffKeys.timesheet(id ?? '__none__', from, to),
    queryFn: () => getStaffTimesheet(id as string, from, to),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}
